import { useState } from "react"
import type { PuzzleType, GridStyle } from "@shared/constants.ts"
import { BRITISH_SUPPORTED_SIZES } from "@shared/constants.ts"

interface PuzzleGeneratorProps {
  onGenerate: (gridSize: number, puzzleType: PuzzleType, gridStyle: GridStyle) => void
  isLoading: boolean
}

export function PuzzleGenerator({ onGenerate, isLoading }: PuzzleGeneratorProps) {
  const [gridSize, setGridSize] = useState(13)
  const [puzzleType, setPuzzleType] = useState<PuzzleType>("quick")
  const [gridStyle, setGridStyle] = useState<GridStyle>("british")

  const britishSupported = (BRITISH_SUPPORTED_SIZES as readonly number[]).includes(gridSize)

  return (
    <div className="generator">
      <h1>cword</h1>
      <p className="subtitle">Crossword Puzzle Generator</p>

      <div className="form-group">
        <label htmlFor="gridSize">Grid Size</label>
        <select
          id="gridSize"
          value={gridSize}
          onChange={(e) => {
            const newSize = Number(e.target.value)
            setGridSize(newSize)
            if (gridStyle === "british" && !(BRITISH_SUPPORTED_SIZES as readonly number[]).includes(newSize)) {
              setGridStyle("american")
            }
          }}
          disabled={isLoading}
        >
          {[5, 7, 9, 11, 13, 15].map((s) => (
            <option key={s} value={s}>
              {s} x {s}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Grid Style</label>
        <div className="radio-group">
          {(["american", "british"] as const).map((style) => (
            <label key={style} className="radio-label">
              <input
                type="radio"
                name="gridStyle"
                value={style}
                checked={gridStyle === style}
                onChange={() => setGridStyle(style)}
                disabled={isLoading || (style === "british" && !britishSupported)}
              />
              <span>{style.charAt(0).toUpperCase() + style.slice(1)}</span>
            </label>
          ))}
          {!britishSupported && gridStyle === "american" && (
            <span className="hint">British style available for 13x13 and 15x15</span>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>Crossword Type</label>
        <div className="radio-group">
          {(["quick", "cryptic", "quiptic", "desi"] as const).map((type) => (
            <label key={type} className="radio-label">
              <input
                type="radio"
                name="puzzleType"
                value={type}
                checked={puzzleType === type}
                onChange={() => setPuzzleType(type)}
                disabled={isLoading}
              />
              <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
            </label>
          ))}
        </div>
      </div>

      <button
        className="generate-btn"
        onClick={() => onGenerate(gridSize, puzzleType, gridStyle)}
        disabled={isLoading}
      >
        {isLoading ? "Generating..." : "Generate Crossword"}
      </button>
    </div>
  )
}
